import { Location } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  allowedImageFormats,
  getFormattedDate,
  getInstanceRegion,
  getRegionCurrency,
  uploadedFileCheck,
} from '../../../../../../global.variable';
import { AppService } from '../../../../../app.service';
import { UserProfileService } from '../../../../../profile/user-profile/user-profile.service';
import { ConfirmDialogComponent } from '../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ModalDialogueService } from '../../../../../shared/components/modal-dialogue/modal-dialogue.service';
import { LoadingSpinnerService } from '../../../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../../../shared/services/toaster.service';
import { ClientsService } from '../../../clients/clients.service';
import { JobsService } from '../../jobs.service';

@Component({
  selector: 'gtapp-print-invoice',
  standalone: false,
  templateUrl: './print-invoice.component.html',
  styleUrl: './print-invoice.component.scss',
})
export class PrintInvoiceComponent implements OnInit, OnDestroy {
  @ViewChild('updateABNTemplate') updateABNTemplate: any = TemplateRef<any>;
  invoiceClientData: any;
  invoiceNum: any;
  invoiceDate: any;
  selectedEvents: any = [];
  additionalLineItems: any = [];
  regionCurrencySymbol = getRegionCurrency(true);
  onsiteDurationDict: any;

  routeSubscription: any;
  companyDetails: any = {};

  abnForm: UntypedFormGroup = new UntypedFormGroup({
    abn: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/^\d+$/),
      Validators.minLength(11),
      Validators.maxLength(11),
    ]),
  });
  invoiceId: string = '';

  selectedReportRecipientList: any = [];
  reportRecipientList: any = [];
  invoiceStatus: number = 0;

  companyForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    abn: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/^\d+$/),
      Validators.minLength(11),
      Validators.maxLength(11),
    ]),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    phone: new UntypedFormControl('', [
      Validators.minLength(7),
      Validators.maxLength(10),
      //Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$'),
      Validators.pattern('^(\\+?(61|64|675)|0)?[0-9]{7,10}$'),
      Validators.required,
    ]),
    addressLookup: new UntypedFormControl(''),
    address1: new UntypedFormControl(''),
    address2: new UntypedFormControl('', [Validators.required]),
    address3: new UntypedFormControl(''),
    city_name: new UntypedFormControl('', [Validators.required]),
    postcode: new UntypedFormControl(''),
    longitude: new UntypedFormControl('', [Validators.required]),
    latitude: new UntypedFormControl('', [Validators.required]),
    country_name: new UntypedFormControl(getInstanceRegion(), [
      Validators.required,
    ]),
    state_code: new UntypedFormControl('', [Validators.required]),
    state_name: new UntypedFormControl('', [Validators.required]),
  });
  companyDialogueStep: number = 1;
  constructor(
    private jobService: JobsService,
    private spinnerService: LoadingSpinnerService,
    private router: Router,
    private viewContainerRef: ViewContainerRef,
    private toasterService: ToasterService,
    private clientService: ClientsService,
    private dialogService: ModalDialogueService,
    private _location: Location,
    private profileService: UserProfileService,
    private route: ActivatedRoute,
    private appService: AppService,
  ) {
    this.routeSubscription = this.route.params.subscribe({
      next: (params) => {
        if (params?.['invKey']) {
          this.getInvoiceAndGetJobs({ id: atob(params?.['invKey']) });
        }
      },
    });
  }
  ngOnInit(): void {
    this.getCompanyDetails();
  }
  ngOnDestroy(): void {
    if (this.routeSubscription) this.routeSubscription?.unsubscribe();
  }

  getTotalInvoiceAmount(amountType: string) {
    const events = this.selectedEvents?.concat(this.additionalLineItems);
    const totalAmount = events?.reduce((sum: any, item: any) => {
      // Ensure item.amount is a valid number before adding
      let amount = 0;
      if (amountType == 'sale') {
        amount = Number(item?.sale_amount);
      } else if (amountType == 'tax') {
        amount = Number(item?.tax_amount);
      } else {
        const taxAmt = Number(item?.tax_amount) || 0;
        const saleAmt = Number(item?.sale_amount) || amount;
        amount = taxAmt + saleAmt;
      }

      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    return totalAmount?.toFixed(2);
  }
  getInvoiceAndGetJobs(invoiceData: any) {
    this.spinnerService.show();
    this.invoiceId = invoiceData?.id;
    this.jobService
      .getManageInvoiceEvents(
        { invoice_id: invoiceData?.id },
        { get_jobs_under_invoice: 1, get_invoice_info: 1 },
        false,
      )
      .then((response: any) => {
        if (response?.status === 'success') {
          this.spinnerService.show('Fetching Details');
          this.invoiceNum = response?.invoice_info?.invoice_num;
          this.invoiceDate = getFormattedDate(
            response?.invoice_info?.invoiced_at,
          );

          this.additionalLineItems = [];
          // additional line items that may be present in the invoice
          if (response?.additional_line_items?.length) {
            this.additionalLineItems = JSON.parse(
              JSON.stringify(response?.additional_line_items),
            );
          }

          this.selectedEvents = JSON.parse(JSON.stringify(response?.data));

          if (
            response?.onsite_duration_dict &&
            Object.keys(response?.onsite_duration_dict)?.length
          ) {
            this.onsiteDurationDict = response?.onsite_duration_dict;
          }

          this.invoiceClientData = response?.invoice_info?.company;
          this.invoiceStatus = response?.invoice_info?.status;

          // this.titleService.setTitle(
          //   'inv_' +
          //     this.invoiceClientData?.company_name?.toLowerCase() +
          //     '_' +
          //     this.invoiceDate +
          //     '_' +
          //     this.invoiceNum
          // );

          this.checkAndOpenPrintDialog();
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response?.message,
          });
        }
      });
  }
  checkAndOpenPrintDialog() {
    if (this.companyDetails?.id) {
      this.spinnerService.hide();

      if (
        !this.companyDetails?.abn &&
        this.companyDetails?.address?.country_name?.toLowerCase() == 'australia'
      ) {
        const dialogRef = this.dialogService.open(
          this.updateABNTemplate,
          {},
          this.viewContainerRef,
        );
        dialogRef.afterClosed().subscribe((res: any) => {
          if (res) {
            this.updateABN();
          } else {
            this.goBack();
          }
        });
      }
    } else {
      setTimeout(() => {
        this.checkAndOpenPrintDialog();
      }, 500);
    }
  }
  getCompanyDetails() {
    this.profileService.fetchCompanyDetails().subscribe((res: any) => {
      if (res['status'] === 'success') {
        this.companyDetails = res?.data;
        this.companyForm.reset();
        this.formatCompanyData();
      } else {
        this.spinnerService.hide();
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: res['message'],
        });
      }
    });
  }
  openPrintDialog() {
    window.print();
  }
  goBack() {
    this._location.back();
  }
  clearAndFocusTextBox(elementId: string) {
    this.abnForm.controls['abn'].setValue('');
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
  updateABN() {
    this.spinnerService.show();
    this.clientService
      .updateCompanyDetails({ abn: this.abnForm.value.abn }, { change_abn: 1 })
      .then((response: any) => {
        if (response['status'] == 'success') {
          this.spinnerService.hide();
          this.companyDetails = response?.data;
          this.companyForm.reset();
          this.formatCompanyData();
          this.checkAndOpenPrintDialog();
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
  }
  openCamPopup() {
    var button: any = document.getElementById('fileInput');
    button.click();
  }

  async handleFileInput(item: any) {
    let file = item.files.item(0);
    let fileSize: number = file.size;
    let fileName: string = file.name;
    let fileExtension: string = fileName.split('.').pop() || '';
    let fileCheckError = await uploadedFileCheck(fileExtension, fileSize);
    if (fileCheckError) {
      this.toasterService.setMessage({
        successMessage: '',
        errorMessage: fileCheckError,
      });
    } else {
      this.onFileSubmit(item.files.item(0));
    }
  }
  onFileSubmit(file: any) {
    if (file) {
      this.spinnerService.show();
      let fileData: FormData = new FormData();
      fileData.append('file', file);

      this.appService
        .formDataApi('company_profile', fileData, false, { change_logo: 1 })
        .then((response: any) => {
          if (response['status'] == 'success') {
            this.companyDetails = response?.data;
            this.companyForm.reset();
            this.formatCompanyData();
          } else {
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response['message'],
            });
          }
          this.spinnerService.hide();
        });
    }
  }
  voidInvoice() {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Void Invoice?',
        message: `You can create another invoice with the same items.`,
        btn2Detail: { class: 'btn-danger' },
      },
    });
    dialogRef.afterClosed().subscribe((value: any) => {
      if (value === true) {
        let body: any = { invoice_id: this.invoiceId };

        this.spinnerService.show();
        this.jobService
          .getManageInvoiceEvents(body, { void_invoice: 1 }, true)
          .then((response: any) => {
            if (response?.status === 'success') {
              this.goBack();
            } else {
              this.goBack();
              this.toasterService.setMessage({
                successMessage: '',
                errorMessage: response?.message,
              });
            }
          });
      }
    });
  }
  selectUnselectRecipients(contact: any) {
    contact.isSnubbed = !contact?.isSnubbed;

    this.selectedReportRecipientList = [
      ...new Set(
        this.reportRecipientList
          ?.filter((recipient: any) => !recipient?.isSnubbed)
          ?.map((recipient: any) => recipient?.id),
      ),
    ];
  }
  sendMultipleDockets(recipientListTemplate: any) {
    let body: any = {};

    body['invoice_ids'] = [this.invoiceId];
    this.spinnerService.show();
    this.jobService
      .sendJobMultipleReports(body, { get_recipients: 1 })
      .then((response: any) => {
        if (response?.status === 'success') {
          this.reportRecipientList = response?.data;
          this.selectedReportRecipientList = [
            ...new Set(
              this.reportRecipientList
                ?.filter((recipient: any) => !recipient?.isSnubbed)
                ?.map((recipient: any) => recipient?.id),
            ),
          ];
          const dialogRef = this.dialogService.open(
            recipientListTemplate,
            {},
            this.viewContainerRef,
          );
          dialogRef.afterClosed().subscribe((value: any) => {
            if (value === true && this.selectedReportRecipientList?.length) {
              this.spinnerService.show();
              body.recipient_ids = this.selectedReportRecipientList;
              this.jobService
                .sendJobMultipleReports(body, {})
                .then((response: any) => {
                  if (response?.status === 'success') {
                    this.toasterService.setMessage({
                      successMessage: response?.message,
                      errorMessage: '',
                    });
                    this.spinnerService.hide();
                  } else {
                    this.toasterService.setMessage({
                      successMessage: '',
                      errorMessage: response?.message,
                    });
                    this.spinnerService.hide();
                  }
                });
            }
          });
        } else {
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response?.message,
          });
        }
        this.spinnerService.hide();
      });
  }
  updateCompanyData() {
    if (this.companyForm.valid) {
      this.spinnerService.show();
      var requestData = this.companyForm.value;
      this.clientService
        .updateCompanyDetails(requestData)
        .then((response: any) => {
          if (response['status'] == 'success') {
            this.companyDetails = response?.data;
            this.companyForm.reset();
            this.formatCompanyData();
          } else {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              successMessage: '',
              errorMessage: response['message'],
            });
          }
        });
    }
  }
  formatCompanyData() {
    this.companyForm.patchValue({
      name: this.companyDetails?.name,
      abn: this.companyDetails?.abn,
      email: this.companyDetails?.email,
      phone: this.companyDetails?.phone,
      address1: this.companyDetails?.address?.address1 || '',
      address2: this.companyDetails?.address?.address2,
      address3: this.companyDetails?.address?.address3,
      city_name: this.companyDetails?.address?.city_name,
      postcode: this.companyDetails?.address?.postcode,
      state_code: this.companyDetails?.address?.state_code,
      state_name:
        this.companyDetails?.address?.state_name ||
        this.companyDetails?.address?.state_code,
      country_name:
        this.companyDetails?.address?.country_name || getInstanceRegion(),
      latitude: this.companyDetails?.address?.latitude,
      longitude: this.companyDetails?.address?.longitude,
    });
    this.spinnerService.hide();
  }
  openTemplate(template: TemplateRef<any>) {
    this.formatCompanyData();
    this.companyDialogueStep = 1;
    this.dialogService.open(
      template,
      {
        data: {},
      },
      this.viewContainerRef,
    );
  }
}
